import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';
import RadialMenu from './RadialMenu';
import './AdminCodes.css';

const API_URL = process.env.REACT_APP_API_URL;

const ICONS = {
  logout: 'https://cdn-icons-png.flaticon.com/512/9070/9070510.png',
  generateCode: 'https://cdn-icons-png.flaticon.com/512/16800/16800790.png',
  manageCodes: 'https://cdn-icons-png.flaticon.com/512/9464/9464467.png',
  qrCode: 'https://cdn-icons-png.flaticon.com/512/15953/15953021.png',
  qrCodeManage: 'https://cdn-icons-png.flaticon.com/512/3697/3697141.png',
  download: 'https://cdn-icons-png.flaticon.com/512/9131/9131795.png',
  copyCode: 'https://cdn-icons-png.flaticon.com/512/8733/8733469.png',
  shareCode: 'https://cdn-icons-png.flaticon.com/512/8637/8637814.png',
  deleteCode: 'https://cdn-icons-png.flaticon.com/512/3132/3132919.png',
  pauseCode: 'https://cdn-icons-png.flaticon.com/512/2404/2404385.png',
  reactivateCode: 'https://cdn-icons-png.flaticon.com/512/702/702132.png',
  report: 'https://cdn-icons-png.flaticon.com/512/1321/1321938.png'
};

function AdminCodes() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [activeTab, setActiveTab] = useState('generate');
  const [generatedCode, setGeneratedCode] = useState('');
  const [codeName, setCodeName] = useState('');
  const [notes, setNotes] = useState('');
  const [previousCodes, setPreviousCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showCodeCard, setShowCodeCard] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState('all');
  const [showQRModal, setShowQRModal] = useState(false);
  const [selectedQRCode, setSelectedQRCode] = useState('');

  const handleGenerateCode = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Session expired. Please log in again.');
        setTimeout(() => {
          logout();
          navigate('/admin');
        }, 2000);
        return;
      }

      const response = await axios.post(`${API_URL}/api/auth/generate-code`, {
        codeName: codeName.trim(),
        notes: notes.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const code = response.data.accessCode;
      setGeneratedCode(code);
      setShowCodeCard(true);
      setNotes('');
      if (activeTab === 'manage') {
        fetchCodes();
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to generate code';
      setError(errorMessage);
      if (error.response?.status === 401) {
        setTimeout(() => {
          logout();
          navigate('/admin');
        }, 2000);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code || generatedCode);
  };

  const handleShareCode = (code) => {
    const codeToShare = code || generatedCode;
    const message = `Your METATIP access code: ${codeToShare}`;
    if (navigator.share) {
      navigator.share({
        title: 'METATIP Access Code',
        text: message,
      }).catch(() => {
        const url = `https://wa.me/?text=${encodeURIComponent(message)}`;
        window.open(url, '_blank');
      });
    } else {
      const url = `https://wa.me/?text=${encodeURIComponent(message)}`;
      window.open(url, '_blank');
    }
  };

  const handleShowQRCode = (code) => {
    setSelectedQRCode(code || generatedCode);
    setShowQRModal(true);
  };

  const handleDownloadQRCode = () => {
    const qrElement = document.querySelector('.admin-codes-qr-image');
    if (qrElement) {
      try {
        const svgData = new XMLSerializer().serializeToString(qrElement);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();
        const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
        const url = URL.createObjectURL(svgBlob);

        img.onload = () => {
          canvas.width = 280;
          canvas.height = 280;
          ctx.drawImage(img, 0, 0, 280, 280);
          URL.revokeObjectURL(url);
          const pngUrl = canvas.toDataURL('image/png');
          const link = document.createElement('a');
          link.href = pngUrl;
          link.download = `metatip-qr-${selectedQRCode}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        };

        img.onerror = () => {
          console.error('Failed to load QR code image');
        };

        img.src = url;
      } catch (error) {
        console.error('Download failed:', error);
      }
    }
  };

  const handleDeactivateCode = async (codeId) => {
    if (!window.confirm('Are you sure you want to deactivate this code?')) {
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API_URL}/api/auth/deactivate/${codeId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchCodes();
    } catch (error) {
      setError('Failed to deactivate code');
    }
  };

  const handleReactivateCode = async (codeId) => {
    if (!window.confirm('Reactivate this code?')) {
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API_URL}/api/auth/reactivate/${codeId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchCodes();
    } catch (error) {
      setError('Failed to reactivate code');
    }
  };

  const handleDeleteCode = async (codeId) => {
    if (!window.confirm('Are you sure you want to permanently delete this code?')) {
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/auth/delete/${codeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchCodes();
    } catch (error) {
      setError('Failed to delete code');
    }
  };

  const handleDownloadCSV = () => {
    if (!previousCodes || previousCodes.length === 0) {
      setError('No data to export');
      return;
    }

    const headers = ['Code Name', 'Access Code', 'Status', 'Guest Name', 'Guest Email', 'Guest Phone', 'Terms Accepted', 'Notes', 'Registered At', 'Last Used', 'Created At'];

    const csvRows = [headers.join(',')];

    previousCodes.forEach(code => {
      const fullName = [code.firstName, code.lastName].filter(Boolean).join(' ').trim() || 'N/A';
      const row = [
        `"${code.codeName || ''}"`,
        `"${code.accessCode}"`,
        code.active ? 'Active' : 'Inactive',
        `"${fullName}"`,
        `"${code.guestEmail || 'N/A'}"`,
        `"${code.guestPhone || 'N/A'}"`,
        code.termsAccepted ? 'Yes' : 'No',
        `"${code.notes || ''}"`,
        code.registrationCompletedAt ? new Date(code.registrationCompletedAt).toLocaleDateString() : 'N/A',
        code.lastUsedAt ? new Date(code.lastUsedAt).toLocaleDateString() : 'Never',
        new Date(code.createdAt).toLocaleDateString()
      ];
      csvRows.push(row.join(','));
    });

    const csvContent = "data:text/csv;charset=utf-8," + csvRows.join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `metatip_users_${new Date().toISOString().slice(0,10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const fetchCodes = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Session expired. Please log in again.');
        return;
      }
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (filterActive !== 'all') params.active = filterActive;
      const response = await axios.get(`${API_URL}/api/auth/list-codes`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setPreviousCodes(response.data.codes || []);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        setError('Session expired. Please log in again.');
      } else {
        setError('Failed to fetch codes. Please try again.');
      }
      setPreviousCodes([]);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, filterActive]);

  useEffect(() => {
    if (activeTab === 'manage') {
      fetchCodes();
    }
  }, [activeTab, fetchCodes]);

  return (
    <div className="admin-codes">
      <div className="animated-gradient-bg" />

      <div className="admin-codes-waves">
        <div className="admin-codes-wave admin-codes-wave-top"></div>
        <div className="admin-codes-wave admin-codes-wave-middle"></div>
        <div className="admin-codes-wave admin-codes-wave-bottom"></div>
      </div>

      <div className="admin-codes-header">
        <img src="/DarkLogo.png" alt="METATIP emblem" className="admin-codes-logo" />
        <div className="admin-codes-title">Code Management</div>
      </div>

      <div className="admin-codes-tabs">
        <button
          className={`admin-codes-tab ${activeTab === 'generate' ? 'active' : ''}`}
          onClick={() => setActiveTab('generate')}
        >
          <span className="admin-codes-tab-icon">
            <img src={ICONS.generateCode} alt="Generate" className="admin-codes-icon admin-codes-icon-medium" />
          </span>
          <span className="admin-codes-tab-label">Generate Code</span>
        </button>
        <button
          className={`admin-codes-tab ${activeTab === 'manage' ? 'active' : ''}`}
          onClick={() => setActiveTab('manage')}
        >
          <span className="admin-codes-tab-icon">
            <img src={ICONS.manageCodes} alt="Manage" className="admin-codes-icon admin-codes-icon-medium" />
          </span>
          <span className="admin-codes-tab-label">Manage Codes</span>
        </button>
      </div>

      <div className="admin-codes-body">
        <AnimatePresence mode="wait">
          {activeTab === 'generate' && (
            <motion.div
              key="generate"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
              className="admin-codes-panel"
            >
              <div className="admin-codes-form">
                <input
                  type="text"
                  placeholder="Code Name (e.g., Anatomy Students - Fall 2025)"
                  value={codeName}
                  onChange={(e) => setCodeName(e.target.value)}
                  className="admin-codes-input"
                  required
                />
                <textarea
                  placeholder="Optional Notes (internal comments)"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="admin-codes-textarea"
                  rows="3"
                />
                <button
                  className="admin-codes-generate-btn"
                  onClick={handleGenerateCode}
                  disabled={loading || !codeName.trim()}
                >
                  {loading ? 'Generating...' : 'Generate Access Code'}
                </button>
              </div>

              {showCodeCard && generatedCode && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="admin-codes-card"
                >
                  <div className="admin-codes-display">
                    <div className="admin-codes-header-row">
                      <span className="admin-codes-label">Code Name:</span>
                      <span className="admin-codes-name">{codeName}</span>
                    </div>
                    <div className="admin-codes-value-section">
                      <span className="admin-codes-label">Access Code:</span>
                      <span className="admin-codes-value">{generatedCode}</span>
                    </div>
                  </div>
                  <div className="admin-codes-actions">
                    <button onClick={() => handleCopyCode()} className="admin-codes-action-btn copy-btn">
                      <img src={ICONS.copyCode} alt="Copy code" className="admin-codes-icon admin-codes-icon-tiny" />
                      Copy
                    </button>
                    <button onClick={() => handleShareCode()} className="admin-codes-action-btn share-btn">
                      <img src={ICONS.shareCode} alt="Share code" className="admin-codes-icon admin-codes-icon-tiny" />
                      Share
                    </button>
                    <button onClick={() => handleShowQRCode()} className="admin-codes-action-btn qr-btn">
                      <img src={ICONS.qrCode} alt="Show QR Code" className="admin-codes-icon admin-codes-icon-tiny" />
                      QR
                    </button>
                  </div>
                </motion.div>
              )}
              {error && <div className="admin-codes-error">{error}</div>}
            </motion.div>
          )}

          {activeTab === 'manage' && (
            <motion.div
              key="manage"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
              className="admin-codes-panel"
            >
              <div className="admin-codes-search-section">
                <input
                  type="text"
                  placeholder="Search codes..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="admin-codes-search-input"
                />
                <select
                  value={filterActive}
                  onChange={(e) => setFilterActive(e.target.value)}
                  className="admin-codes-filter-select"
                >
                  <option value="all">All Codes</option>
                  <option value="true">Active Only</option>
                  <option value="false">Inactive Only</option>
                </select>
                <button
                  className="admin-codes-report-btn"
                  onClick={handleDownloadCSV}
                  title="Download User Report (CSV)"
                >
                  <img src={ICONS.report} alt="Report" className="admin-codes-icon admin-codes-icon-tiny" />
                  Report
                </button>
              </div>

              {loading ? (
                <div className="admin-codes-loading">Loading...</div>
              ) : previousCodes.length === 0 ? (
                <div className="admin-codes-empty">No codes found</div>
              ) : (
                <div className="admin-codes-list">
                  {previousCodes.map((item, index) => (
                    <motion.div
                      key={item._id || index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="admin-codes-item"
                    >
                      <div className="admin-codes-item-info">
                        <div className="admin-codes-item-header">
                          <span className="admin-codes-item-code">{item.accessCode}</span>
                          <span className={`admin-codes-status ${item.active ? 'active' : 'inactive'}`}>
                            {item.active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        {item.codeName && (
                          <span className="admin-codes-item-name">{item.codeName}</span>
                        )}
                        {(item.firstName || item.lastName) && (
                          <span className="admin-codes-item-guest">
                            Guest: {[item.firstName, item.lastName].filter(Boolean).join(' ')}
                          </span>
                        )}
                        <span className="admin-codes-item-date">
                          Created: {new Date(item.createdAt).toLocaleDateString()}
                        </span>
                        {item.lastUsedAt && (
                          <span className="admin-codes-item-used">
                            Last Used: {new Date(item.lastUsedAt).toLocaleDateString()}
                          </span>
                        )}
                        {item.notes && (
                          <span className="admin-codes-item-notes">{item.notes}</span>
                        )}
                      </div>
                      <div className="admin-codes-item-actions">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopyCode(item.accessCode);
                          }}
                          className="admin-codes-icon-btn"
                          title="Copy"
                        >
                          <img src={ICONS.copyCode} alt="Copy code" className="admin-codes-icon admin-codes-icon-small" />
                        </button>
                        {item.active && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleShowQRCode(item.accessCode);
                            }}
                            className="admin-codes-icon-btn qr-icon-btn"
                            title="Show QR Code"
                          >
                            <img src={ICONS.qrCodeManage} alt="Show QR Code" className="admin-codes-icon admin-codes-icon-small" />
                          </button>
                        )}
                        {item.active ? (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeactivateCode(item._id);
                            }}
                            className="admin-codes-icon-btn deactivate-icon-btn"
                            title="Deactivate"
                          >
                            <img src={ICONS.pauseCode} alt="Deactivate code" className="admin-codes-icon admin-codes-icon-small" />
                          </button>
                        ) : (
                          <>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleReactivateCode(item._id);
                              }}
                              className="admin-codes-icon-btn reactivate-icon-btn"
                              title="Reactivate"
                            >
                              <img src={ICONS.reactivateCode} alt="Reactivate code" className="admin-codes-icon admin-codes-icon-small" />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteCode(item._id);
                              }}
                              className="admin-codes-icon-btn delete-icon-btn"
                              title="Delete"
                            >
                              <img src={ICONS.deleteCode} alt="Delete code" className="admin-codes-icon admin-codes-icon-small" />
                            </button>
                          </>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
              {error && <div className="admin-codes-error">{error}</div>}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* QR Code Modal */}
      <AnimatePresence>
        {showQRModal && (
          <motion.div
            className="admin-codes-modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={(e) => {
              if (e.target === e.currentTarget) setShowQRModal(false);
            }}
          >
            <motion.div
              className="admin-codes-modal"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            >
              <button
                className="admin-codes-modal-close"
                onClick={() => setShowQRModal(false)}
                aria-label="Close QR Code modal"
              >
                &#10005;
              </button>

              <div className="admin-codes-modal-content">
                <h3 className="admin-codes-modal-title">Scan to Access METATIP</h3>
                <p className="admin-codes-modal-subtitle">Access Code: <strong>{selectedQRCode}</strong></p>

                <div className="admin-codes-qr-container">
                  <QRCodeSVG
                    value={`https://metatip3d.com.tr/?code=${selectedQRCode}`}
                    size={280}
                    level="H"
                    includeMargin={true}
                    className="admin-codes-qr-image"
                  />
                </div>

                <div className="admin-codes-modal-actions">
                  <button
                    onClick={handleDownloadQRCode}
                    className="admin-codes-modal-action-btn download-btn"
                  >
                    <img src={ICONS.download} alt="Download" className="admin-codes-icon admin-codes-icon-tiny" />
                    Download QR
                  </button>
                  <button
                    onClick={() => setShowQRModal(false)}
                    className="admin-codes-modal-action-btn close-btn"
                  >
                    Close
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <RadialMenu />
    </div>
  );
}

export default AdminCodes;